import React, { useEffect, useState } from 'react';
import { Alert, Button, TextField, useTheme, CircularProgress } from '@mui/material';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { propertiesPath, youtubePath } from '../../../constants';
import { getUrl } from '../../../helpers/api/api';
import { checkLiveNow_endpoint, liveStatus_endpoint, setLiveLink_endpoint, setLiveOn_endpoint, setliveStatus_endpoint } from '../../../helpers/api/apiConstants';
import VisuallyHiddenText from '../../../components/VisuallyHiddenText';
import RequireAuth from '../requireAuth';
import DashboardHeader from '../dashboardHeader';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import NumberIcon from '@mui/icons-material/FormatListNumbered';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import IconButton from '@mui/material/IconButton';
import { faYoutube } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const GoLive = () => {
    const theme = useTheme();

    const [liveStatus, setLiveStatus] = useState();
    const [live, setLive] = useState();
    const [liveOn, setLiveOn] = useState();
    const [liveLink, setLiveLink] = useState();
    const [iframeInput, setIframeInput] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [isCheckingNow, setIsCheckingNow] = useState(false);
    const [checkNowResult, setCheckNowResult] = useState(null);

    const mainStyle = {
        fontFamily: theme.typography.fontFamily,
        backgroundColor: theme.palette.brand.linen,
        paddingTop: 'var(--nav-height)',
        minHeight: '100vh',
        boxSizing: 'border-box',
    };

    const eyebrowStyle = {
        fontFamily: theme.typography.fontFamily,
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: theme.palette.brand.brass,
        margin: '0 0 6px',
    };

    const sectionHeadingStyle = {
        fontFamily: theme.typography.secondaryFontFamily,
        fontWeight: 600,
        color: theme.palette.primary.dark,
        fontSize: '22px',
        margin: '0 0 16px',
    };

    const sectionStyle = {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.brand.border}`,
        borderRadius: '10px',
        padding: '24px',
        marginBottom: '24px',
    };

    const checkNowButtonStyle = {
        background: `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 50%, ${theme.palette.primary.light} 100%)`,
        backgroundSize: '200% auto',
        backgroundPosition: 'left center',
        color: theme.palette.background.paper,
        fontFamily: theme.typography.fontFamily,
        fontWeight: 700,
        textTransform: 'none',
        borderRadius: '999px',
        padding: '10px 28px',
        boxShadow: `0 4px 14px ${theme.palette.brand.mist}`,
        transition: 'background-position 0.6s ease, box-shadow 0.2s ease, transform 0.15s ease',
        '&:hover': {
            backgroundPosition: 'right center',
            boxShadow: `0 6px 20px ${theme.palette.brand.mist}`,
            transform: 'translateY(-1px)',
        },
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleLiveChange = () => {
        setLive(live ? false : true);
    };

    const handleLiveLinkChange = (event) => {
        setLiveLink(event.target.value);
    };

    const handleIframeInputChange = (event) => {
        setIframeInput(event.target.value);
    };

    const handleIframeToLink = () => {
        const srcMatch = iframeInput.match(/src="([^"]*)"/i);
        if (srcMatch && srcMatch[1]) {
            setLiveLink(srcMatch[1]);
        }
    };

    // Runs the same YouTube live-broadcast check the scheduled server-side
    // job runs during service windows (see server/services/liveCheckJob.js),
    // but on demand - for a special event, or any time outside the regular
    // schedule where nobody wants to hunt down the embed link by hand.
    const handleCheckNow = async () => {
        setIsCheckingNow(true);
        setCheckNowResult(null);

        try {
            const response = await fetch(getUrl(checkLiveNow_endpoint), { method: 'POST' });
            if (!response.ok) throw response;
            const data = await response.json();

            setLive(data.isLive);
            setLiveLink(data.link || '');
            setCheckNowResult({
                success: true,
                message: data.isLive
                    ? 'Found a live broadcast on YouTube - status and link below are updated.'
                    : 'No live broadcast found on YouTube right now.',
            });
        } catch (error) {
            console.error('Error checking YouTube live status: ', error);
            setCheckNowResult({ success: false, message: "Couldn't reach YouTube to check right now. Please try again." });
        } finally {
            setIsCheckingNow(false);
        }
    };

    useEffect(() => {
        if (!liveStatus) {
            fetch(getUrl(liveStatus_endpoint))
                .then(response => {
                    if (response.ok) {
                        return response.json();
                    }
                    throw response;
                })
                .then(data => {
                    setLiveStatus(true);
                    setLive(data.IS_LIVE);
                    setLiveOn(data.LIVE_ON);
                    setLiveLink(data.LINK);
                })
                .catch(error => {
                    console.error("Error setting live status. Error: ", error);
                });
        }
    }, [liveStatus]);

    useEffect(() => {
        async function setLiveStatusDb() {
            const liveJson = {
                live_status: live
            };

            await fetch(getUrl(setliveStatus_endpoint), {
                method: 'POST',
                body: JSON.stringify(liveJson),
                headers: { 'Content-Type': 'application/json' }
            }).then(res => res.json())
                .then(json => console.log('Live Status Update: ', json));
        }

        if (live !== undefined) {  // Only call setLiveStatusDb if live is defined
            setLiveStatusDb();
        }
    }, [live]);

    useEffect(() => {
        async function setLiveOnDb() {
            const liveOnJson = {
                live_on: liveOn
            };

            await fetch(getUrl(setLiveOn_endpoint), {
                method: 'POST',
                body: JSON.stringify(liveOnJson),
                headers: { 'Content-Type': 'application/json' }
            }).then(res => res.json())
                .then(json => console.log('Live On Update: ', json));
        }
        setLiveOnDb();
    }, [liveOn]);

    const handleLiveLinkSave = async () => {
        const liveLinkJson = {
            live_link: liveLink
        };

        await fetch(getUrl(setLiveLink_endpoint), {
            method: 'POST',
            body: JSON.stringify(liveLinkJson),
            headers: { 'Content-Type': 'application/json' }
        }).then(res => res.json())
            .then(json => console.log('Live Link Update: ', json));
    };

    return (
        <div style={mainStyle}>
            <RequireAuth />

            <div style={{ maxWidth: '700px', margin: '0 auto', padding: '36px 24px 56px' }}>
                <DashboardHeader
                    eyebrow="Properties"
                    heading="Live Feed Settings"
                    showBackLink
                    backTo={propertiesPath}
                    backLabel="Properties"
                />

                {/* Outside of a scheduled service, the server-side job
                    (server/services/liveCheckJob.js) isn't polling YouTube
                    on its own - this lets an admin trigger that same check
                    on demand, auto-filling the link/status below instead
                    of walking through the manual steps. */}
                <div style={sectionStyle}>
                    <div style={eyebrowStyle}>Quick Check</div>
                    <h2 style={sectionHeadingStyle}>Check YouTube Now</h2>

                    <Typography variant="body2" sx={{ fontFamily: theme.typography.paragraphFontFamily, color: theme.palette.text.secondary, marginBottom: '16px' }}>
                        Going live outside a regular service time? Check YouTube right now to automatically fill in the link and status below.
                    </Typography>

                    <Button
                        onClick={handleCheckNow}
                        disabled={isCheckingNow}
                        sx={checkNowButtonStyle}
                        startIcon={isCheckingNow ? <CircularProgress size={18} sx={{ color: theme.palette.background.paper }} /> : <AutorenewIcon />}
                    >
                        {isCheckingNow ? 'Checking YouTube…' : 'Check YouTube Now'}
                    </Button>

                    {checkNowResult && (
                        <Alert severity={checkNowResult.success ? 'success' : 'error'} sx={{ marginTop: '16px' }}>
                            {checkNowResult.message}
                        </Alert>
                    )}
                </div>

                {/* Set the link FIRST - this used to sit below the Go Live
                    button, which meant the primary (and most visible-to-
                    the-public) action came before the thing it depends on
                    was even confirmed. */}
                <div style={sectionStyle}>
                    <div style={eyebrowStyle}>Step 1</div>
                    <h2 style={sectionHeadingStyle}>Set Your Stream Link</h2>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                        <a href={youtubePath} target="_blank" rel="noreferrer" style={{ color: theme.palette.primary.dark, display: 'flex' }}>
                            <FontAwesomeIcon icon={faYoutube} aria-hidden="true" size="lg" />
                            <VisuallyHiddenText>YouTube</VisuallyHiddenText>
                        </a>
                        <span style={{ fontFamily: theme.typography.paragraphFontFamily, fontSize: '13px', color: theme.palette.text.secondary }}>
                            Streaming via YouTube
                        </span>
                    </div>

                    <Alert severity="info" sx={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                                How to get the embedded YouTube link
                            </Typography>
                            <IconButton onClick={handleToggle} size="small">
                                <ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </IconButton>
                        </div>
                        <Collapse in={isOpen}>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon><NumberIcon>1</NumberIcon></ListItemIcon>
                                    <ListItemText primary="Click the YouTube icon above." />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><NumberIcon>2</NumberIcon></ListItemIcon>
                                    <ListItemText primary="From the YouTube page, click the current live video." />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><NumberIcon>3</NumberIcon></ListItemIcon>
                                    <ListItemText primary="Click the 'Share' button under the video." />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><NumberIcon>4</NumberIcon></ListItemIcon>
                                    <ListItemText primary="Click the 'Embed' icon in the sharing options." />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><NumberIcon>5</NumberIcon></ListItemIcon>
                                    <ListItemText primary="Copy the provided iframe code." />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><NumberIcon>6</NumberIcon></ListItemIcon>
                                    <ListItemText primary="Paste the copied iframe code into the box below." />
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon><NumberIcon>7</NumberIcon></ListItemIcon>
                                    <ListItemText primary="Press 'Transform to Link' to extract the video URL from the iframe code." />
                                </ListItem>
                            </List>
                        </Collapse>
                    </Alert>

                    <TextField
                        multiline
                        rows={4}
                        fullWidth
                        placeholder='<iframe width="560" height="315" src="" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe>'
                        label="YouTube embedded frame"
                        value={iframeInput}
                        onChange={handleIframeInputChange}
                        sx={{ marginBottom: '12px' }}
                    />

                    <Button onClick={handleIframeToLink} variant="outlined" sx={{ marginBottom: '20px' }}>
                        Transform to Link
                    </Button>

                    <TextField
                        label="Live Link"
                        fullWidth
                        value={liveLink ? liveLink : ''}
                        onChange={handleLiveLinkChange}
                        sx={{ marginBottom: '16px' }}
                    />

                    <Button onClick={handleLiveLinkSave} variant="contained">
                        Save Live Link
                    </Button>
                </div>

                {/* Go Live comes second, once the link above is actually
                    set - flipping this on with a stale/empty link was the
                    old failure mode. */}
                <div style={sectionStyle}>
                    <div style={eyebrowStyle}>Step 2</div>
                    <h2 style={sectionHeadingStyle}>Broadcast Status</h2>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                        <span
                            style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: live ? theme.palette.warning.main : theme.palette.text.secondary,
                                display: 'inline-block',
                            }}
                        />
                        <span style={{ fontFamily: theme.typography.fontFamily, fontSize: '14px', fontWeight: 600, color: theme.palette.text.primary }}>
                            {!liveStatus ? 'Checking status...' : live ? 'Currently live' : 'Currently offline'}
                        </span>
                    </div>

                    {!liveStatus && <Button startIcon={<CircularProgress size={18} />} disabled>Getting Live Status...</Button>}
                    {liveStatus && (
                        <Button
                            startIcon={<LiveTvIcon />}
                            variant="contained"
                            size="large"
                            onClick={handleLiveChange}
                            color={live ? 'warning' : 'primary'}
                        >
                            {live ? 'Stop Going Live' : 'Go Live Now'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GoLive;
